import { createContext, useCallback, useContext, type CSSProperties } from 'react'

/**
 * A presentation choice, never a second graph. Background nodes keep owning
 * upload lifecycles and canvas geometry while simplified cards share their data.
 */
export type NodePresentation = 'canvas' | 'background' | 'list' | 'detail'

export const NodePresentationContext = createContext<NodePresentation>('canvas')
export const useNodePresentation = () => useContext(NodePresentationContext)

/** Preserve canvas geometry exactly; only the detail presentation stretches. */
export function useNodePresentationWidth(): (nativeWidth: number) => number | string {
  const presentation = useNodePresentation()
  return useCallback(
    (nativeWidth: number) => presentation === 'detail' ? '100%' : nativeWidth,
    [presentation],
  )
}

/** Detail media has one height across aspect ratios; canvas/list keep native geometry. */
export function useNodePresentationPreview(): {
  previewStyle: CSSProperties
  mediaFit: 'contain' | 'cover'
} {
  const presentation = useNodePresentation()
  return presentation === 'detail'
    ? { previewStyle: { height: 'min(480px, 60vh)', aspectRatio: 'auto' }, mediaFit: 'contain' }
    : { previewStyle: {}, mediaFit: 'cover' }
}
